import { useEffect, useRef } from 'react';
import Icon from '../AppIcon';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | 'disclaimer';
}

const LegalModal = ({ isOpen, onClose, type }: LegalModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getContent = () => {
    switch (type) {
      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                At Dhavaja, we are committed to protecting your privacy and ensuring the security of your personal information.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Information Collection</h3>
              <p className="text-muted-foreground">
                We collect information you provide directly to us through contact forms, including your name, email address, company details, and project requirements.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Use of Information</h3>
              <p className="text-muted-foreground">
                Your information is used solely to respond to your inquiries, provide requested services, and communicate about potential R&D projects. We do not sell or share your personal information with third parties.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Data Security</h3>
              <p className="text-muted-foreground">
                We implement industry-standard security measures to protect your information from unauthorized access, disclosure, or modification.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Contact</h3>
              <p className="text-muted-foreground">
                For privacy-related questions, please contact us at contact@dhavaja.com
              </p>
            </div>
          ),
        };
      case 'terms':
        return {
          title: 'Terms of Service',
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                By accessing and using this website, you accept and agree to be bound by the following terms and conditions.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Service Description</h3>
              <p className="text-muted-foreground">
                Dhavaja provides electronics research and development services, including but not limited to embedded systems design, IoT solutions, and custom electronics prototyping.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Intellectual Property</h3>
              <p className="text-muted-foreground">
                All content on this website, including text, graphics, logos, and images, is the property of Dhavaja and protected by intellectual property laws. Project-specific IP rights are governed by individual service agreements.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Confidentiality</h3>
              <p className="text-muted-foreground">
                We maintain strict confidentiality for all client projects. Non-disclosure agreements are available upon request and typically executed before detailed project discussions.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Limitation of Liability</h3>
              <p className="text-muted-foreground">
                While we strive for excellence in all projects, Dhavaja shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services.
              </p>
            </div>
          ),
        };
      case 'disclaimer':
        return {
          title: 'Disclaimer',
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                This website is currently in interim status while our comprehensive platform is under development.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Interim Website Notice</h3>
              <p className="text-muted-foreground">
                The information provided on this landing page represents a subset of Dhavaja's full capabilities. Our complete service portfolio, detailed case studies, and comprehensive technical documentation will be available on the full website launching soon.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Information Accuracy</h3>
              <p className="text-muted-foreground">
                While we make every effort to ensure accuracy, the information on this website is provided "as is" without warranties of any kind. Technical specifications and service details are subject to change.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">Project Feasibility</h3>
              <p className="text-muted-foreground">
                Initial consultations are required to determine project feasibility and provide accurate timelines and cost estimates. Information on this website should not be considered a commitment to specific deliverables without formal agreement.
              </p>
              <h3 className="text-foreground font-semibold text-lg mt-6">External Links</h3>
              <p className="text-muted-foreground">
                This website may contain links to external sites. Dhavaja is not responsible for the content or privacy practices of these external websites.
              </p>
            </div>
          ),
        };
    }
  };

  const { title, content } = getContent();

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-2xl max-h-[90vh] glass-effect rounded-lg shadow-glass-lg overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 id="modal-title" className="text-foreground font-semibold text-2xl">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-250"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {content}
        </div>

        <div className="flex items-center justify-end p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-md bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all duration-250"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;